import api from 'Api/ApiMethods';
import i18next, { t } from 'i18next';
import { makeAutoObservable } from 'mobx';
import { getAxiosErrorMessage } from 'utils/httpErrorsHelpers';
import { NotificationTemplate } from 'models/restTypes';
import { RootStore } from './rootStore';

export class NotificationStore {
  rootStore: RootStore = {} as RootStore;
  notificationTemplates: NotificationTemplate[] = [];
  selectedTemplate: NotificationTemplate | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  getAllNotificationTemplates = async () => {
    this.notificationTemplates = [];
    const response = await api.getNotificationTemplates();
    this.setNotificationTemplates(response);
  };

  resetNotificationTemplates = () => {
    this.notificationTemplates = [];
  };

  setNotificationTemplates = (
    notificationTemplates: NotificationTemplate[],
  ) => {
    this.notificationTemplates = notificationTemplates;
  };

  // Put template Api has no uniqueId for templates , it uses 4 fields to find the correct one
  updateTemplateText = async (newTemplate: NotificationTemplate) => {
    try {
      // This is literally how the backend finds the template to update, since we send an object with no ID, this is the ID
      const foundTemplateIndex = this.notificationTemplates.findIndex(
        (template) =>
          template.notificationTypeId === newTemplate.notificationTypeId &&
          template.templateName === newTemplate.templateName &&
          template.language === newTemplate.language &&
          template.channel === newTemplate.channel &&
          template.providerName === newTemplate.providerName,
      );

      if (foundTemplateIndex === -1) {
        throw new Error('Template not found');
      }

      const updatedTemplate: NotificationTemplate = {
        ...this.notificationTemplates[foundTemplateIndex],
        templateText: newTemplate.templateText,
        imageUri: newTemplate.imageUri,
      };

      await api.putTemplate(updatedTemplate);

      this.notificationTemplates = (this.notificationTemplates || []).map(
        (template, index) => {
          if (index === foundTemplateIndex) {
            return updatedTemplate;
          }

          return template;
        },
      ) as NotificationTemplate[];

      this.successToast('Provider template name updated');
      return true;
    } catch (error) {
      console.log('Error updating template', error);
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  };

  updateProviderTemplateName = async (
    template: NotificationTemplate,
    newProviderTemplateName: string | null,
  ) => {
    try {
      const foundTemplateIndex = this.notificationTemplates.findIndex(
        (tObject) =>
          tObject.notificationTypeId === template.notificationTypeId &&
          tObject.templateName === template.templateName &&
          tObject.language === template.language &&
          tObject.channel === template.channel &&
          tObject.providerName === template.providerName,
      );

      if (foundTemplateIndex === -1) {
        throw new Error('Template not found');
      }

      const updatedTemplate: NotificationTemplate = {
        ...this.notificationTemplates[foundTemplateIndex],
        providerTemplateName: newProviderTemplateName,
      };

      await api.putTemplate(updatedTemplate);

      this.notificationTemplates = this.notificationTemplates.map(
        (tObject, index) =>
          index === foundTemplateIndex ? updatedTemplate : tObject,
      ) as NotificationTemplate[];

      this.successToast(
        i18next.t('notifications:provider_template_name_updated'),
      );

      return true;
    } catch (error) {
      console.log('Error updating provider template name', error);
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  };

  testTemplateNotification = async (
    notificationTypeId: number | string,
    templateName: number | string,
    templateLanguage: string,
  ) => {
    try {
      await api.postTestTemplateNotification(
        notificationTypeId,
        templateName,
        templateLanguage,
      );

      this.successToast(i18next.t('notifications:test_notification_sent'));
      return true;
    } catch (error) {
      this.errorToast(this.getErrorMessage(error));
      return false;
    }
  };

  setSelectedTemplate = (template: NotificationTemplate) => {
    this.selectedTemplate = template;
  };

  private successToast(message: string): void {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
      severity: 'success',
      summary: 'Success',
    });
  }

  errorToast = (message: string) => {
    this.rootStore?.snackBarStore.showToast({
      detail: message,
    });
  };

  private getErrorMessage(error: unknown): string {
    return (
      getAxiosErrorMessage(error) ?? i18next.t('errors:something_went_wrong')
    );
  }
}
